/**
 * Equipment tree implementation using basic Meridian components
 * Provides consistent width and better nested structure display
 */

import React, { useState, useCallback } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import Button from '@amzn/meridian/button';
import folderTokens from "@amzn/meridian-tokens/base/icon/folder";
import plusKnockoutTokens from "@amzn/meridian-tokens/base/icon/plus-knockout"
import cogTokens from "@amzn/meridian-tokens/base/icon/cog";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus"
import caretDownTokens from "@amzn/meridian-tokens/base/icon/caret-down";
import caretRightTokens from "@amzn/meridian-tokens/base/icon/caret-right";
import { EquipmentHierarchy, EquipmentNode } from '../../../services/equipment/types';
import minusTokens from "@amzn/meridian-tokens/base/icon/minus"

interface EquipmentTreeSideMenuProps {
  equipmentHierarchy: EquipmentHierarchy | null;
  selectedNodeId: string | null;
  onNodeSelect: (node: EquipmentNode) => void;
  searchTerm: string;
}

interface TreeNodeItemProps {
  node: EquipmentNode;
  selectedNodeId: string | null;
  onNodeSelect: (node: EquipmentNode) => void;
  searchTerm: string;
  level?: number;
}

const getNodeIcon = (type: string) => {
  switch (type) {
    case 'system':
      return folderTokens;
    case 'asset':
      return plusKnockoutTokens;
    default:
      return documentTokens;
  }
};

const CustomDot = () => (
  <div
    style={{
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#6B7280',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

const getTechnologyBadge = (tech: string) => {
  let backgroundColor = '#6B7280'; // gray
  let textColor = 'white';
  
  switch (tech) {
    case 'U':
      backgroundColor = '#3B82F6'; // blue
      break;
    case 'M':
      backgroundColor = '#8B5CF6'; // purple
      break;
    case 'C':
      backgroundColor = '#F59E0B'; // orange
      break;
    case 'O':
      backgroundColor = '#10B981'; // green
      break;
    default:
      backgroundColor = '#EF4444'; // red for thermal
  }
  
  return (
    <span
      key={tech}
      style={{
        backgroundColor,
        color: textColor,
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: '500',
        marginLeft: '4px',
      }}
    >
      {tech}
    </span>
  );
};

const TreeNodeItem: React.FC<TreeNodeItemProps> = ({ 
  node, 
  selectedNodeId, 
  onNodeSelect, 
  searchTerm, 
  level = 0 
}) => {
  // ALWAYS call hooks first before any conditional logic
  const [isExpanded, setIsExpanded] = useState(false);
  const isSelected = node.id === selectedNodeId;
  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = useCallback(() => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  }, [hasChildren, isExpanded]);

  const handleRowClick = useCallback(() => {
    onNodeSelect(node);
    
    // Additionally expand/collapse if it has children
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  }, [node, onNodeSelect, hasChildren, isExpanded]);

  // Filter based on search term - AFTER all hooks are called
  const shouldShow = !searchTerm || 
    node.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.name.toLowerCase().includes(searchTerm.toLowerCase());
  
  // Now safe to return null after all hooks are called
  if (!shouldShow) return null;

  return (
    <Column spacing="100" width="100%">
      <div 
        style={{
          paddingLeft: `${level * 16}px`,
          backgroundColor: isSelected ? '#E3F2FD' : 'transparent',
          borderRadius: '4px',
          padding: '4px 8px',
          cursor: 'pointer',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
        onClick={handleRowClick}
      >
        {/* Expand/collapse button - now decorative since entire row is clickable */}
        {hasChildren && (
          <Icon tokens={isExpanded ? caretDownTokens : caretRightTokens} />
        )}
        
        {/* Node icon */}
        {node.type === 'position' ? (
          <CustomDot />
        ) :  node.type !== 'asset' && (
          <Icon tokens={getNodeIcon(node.type)} />
        )}
        
        {/* Node name */}
        <Text 
          type="b200" 
          color={isSelected ? "primary" : "secondary"}
        >
          {node.name}
        </Text>
        
        {/* Technology badges */}
        {node.type === 'position' && node.technologies && (
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            {node.technologies.map((tech: string) => getTechnologyBadge(tech))}
          </div>
        )}
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <Column spacing="100" width="100%">
          {node.children!.map((childNode) => (
            <TreeNodeItem
              key={childNode.id}
              node={childNode}
              selectedNodeId={selectedNodeId}
              onNodeSelect={onNodeSelect}
              searchTerm={searchTerm}
              level={level + 1}
            />
          ))}
        </Column>
      )}
    </Column>
  );
};

export const EquipmentTreeSideMenu: React.FC<EquipmentTreeSideMenuProps> = ({
  equipmentHierarchy,
  selectedNodeId,
  onNodeSelect,
  searchTerm,
}) => {
  // If no hierarchy data is available
  if (!equipmentHierarchy || !equipmentHierarchy.rootNode || !equipmentHierarchy.rootNode.children || equipmentHierarchy.rootNode.children.length === 0) {
    return (
      <Column spacing="200" alignmentHorizontal="center" spacingInset="400">
        <Text type="b200" color="secondary">No equipment data available</Text>
      </Column>
    );
  }

  return (
    <div style={{ 
      minWidth: '300px', 
      height: '80vh',  // Use 70% of viewport height for desktop-friendly size
      overflowY: 'auto',  // Specific vertical scroll
      overflowX: 'hidden'  // Prevent horizontal scroll
    }}>
      <Column width="100%" spacing="100">
        {equipmentHierarchy.rootNode.children.map((rootChild) => (
          <TreeNodeItem
            key={rootChild.id}
            node={rootChild}
            selectedNodeId={selectedNodeId}
            onNodeSelect={onNodeSelect}
            searchTerm={searchTerm}
            level={0}
          />
        ))}
      </Column>
    </div>
  );
};
